import React, { useState, useEffect, useContext } from 'react'
import styled from 'styled-components'
import { reactLocalStorage } from 'reactjs-localstorage'
import { Link as A } from 'raviger'
import { I18nContext } from 'i18n'
import { useUser } from 'hooks/useUser'
import { device } from 'utils/device'
import { getCurrentUser } from 'utils/getCurrentUser'
import { CustomLinks } from 'components'
import { getSingleLink } from 'providers/programProvider/links/accessors'
import { useProgramContext } from 'providers/programProvider'

const ProfileIcon = ({ member }) => {
  const [fullName, setFullName] = useState()
  const [first, setFirst] = useState()
  const [last, setLast] = useState()
  const [showDropdown, setShowDropdown] = useState(false)
  const [hideTimeout, setHideTimeout] = useState(null)
  const { translate } = useContext(I18nContext)
  const { executeLogout } = useUser()
  const [state] = useProgramContext()

  const memberId = getCurrentUser()?.memberId

  useEffect(() => {
    const user = reactLocalStorage.getObject('user')
    const { firstName, lastName } = user
    setFirst(firstName ? firstName[0] : '')
    setLast(lastName ? lastName[0] : '')
    setFullName(`${firstName} ${lastName}`)
  }, [])

  const handleMouseEnter = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout)
      setHideTimeout(null)
    }
    setShowDropdown(true)
  }

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowDropdown(false)
    }, 200)
    setHideTimeout(timeout)
  }

  return (
    <Container
      member={member}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DropdownContainer>
        <ProfileBubble title={fullName}>
          {first}
          {last}
        </ProfileBubble>
        {showDropdown && (
          <DropdownMenu
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <DropdownItem
              href={`${getSingleLink(state, 'PROFILE.YM_PROFILE')}${memberId}`}
              target="_blank"
            >
              {translate('edit_profile')}
            </DropdownItem>
            <DropdownItemWrapper>
              <CustomLinks
                location="FOOTER.HELP_INFO"
                options={{ renderers: { paragraph: 'span' } }}
              />
            </DropdownItemWrapper>
            <LogoutButton onClick={executeLogout}>
              {translate('user_logout')}
            </LogoutButton>
          </DropdownMenu>
        )}
      </DropdownContainer>
    </Container>
  )
}

const Container = styled.div`
  position: ${(props) => (!props.member ? 'unset' : 'absolute')};
  right: ${(props) => (!props.member ? '1rem' : '5rem')};
  top: ${(props) => (!props.member ? '1rem' : '4.25rem')};

  @media ${device.mobileL} {
    right: 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: -1rem;
    right: -1rem;
    height: 1rem;
  }
`

const DropdownContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
`

const ProfileBubble = styled.div`
  padding-left: 0.65rem;
  padding-bottom: 0.5rem;
  font-size: 0.9rem;
  font-weight: bold;
  border: 0;
  background-color: ${({ theme }) => theme.palette.alert[0]};
  border-radius: 2.5rem;
  height: 2.5rem;
  width: 2.5rem;
  color: ${({ theme }) => theme.palette.grayscale[0]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
`

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
  margin-top: 0.75rem;
`

const DropdownItem = styled(A)`
  display: block;
  padding: 0.75rem 1rem;
  color: #007681;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale[8]};
    color: #007681;
    text-decoration: underline;
    text-underline-offset: 6px;
    color: ${({ theme }) => theme.palette.primary[0]};
  }
`

const DropdownItemWrapper = styled.div`
  display: block;
  padding: 0.75rem 1rem;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale[8]};
  }

  a {
    color: #007681;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
      text-underline-offset: 6px;
      color: ${({ theme }) => theme.palette.primary[0]};
    }
  }
`

const Divider = styled.div`
  height: 1px;
  background-color: #ddd;
  margin: 0;
`

const LogoutButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: ${({ theme }) => theme.palette.grayscale[2]};
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale[8]};
  }
`

export default ProfileIcon
